import { Injectable } from '@angular/core';

import { Simulation } from '@classes/simulation/simulation';
import { Partition } from '@classes/simulation/partition';
import { KpiByYear } from '@classes/simulation/kpi-by-year';
import { FormatService } from './format.service';
import { OffsetCategory } from '@enums/offset-category.enum';
import { SimulationYear } from '@classes/simulation/simulation-year';
import { SimulationFlagType } from '@enums/simulation-flag-type.enum';
import { PartitionType } from '@enums/partition-type.enum';

@Injectable({
    providedIn: 'root'
})
export class CalculatorService {
    private cumul = 0;
    private isMJP = false;

    constructor(
        private formatSrv: FormatService
    ) { }

    calculate(simulation: Simulation, editedPartCode: string): void {
        this.isMJP = simulation.isMJP;
        this.processPartitionsAmounts(simulation.partitions, editedPartCode);
        this.processAggregats(simulation.partitions);
        this.processKpis(simulation);

        if(simulation.schemaM2) {
            this.processM2(simulation);
        }
    }

    calculateAmount(amount: number, projectionRate: number, indice: number): number {
        return amount * (1 + projectionRate + indice);
    }

    calculateIndice(indice: number, offsetCategory: OffsetCategory, prevIndice: number, grandPrevIndice: number, prevDelta: number): { indice: number, delta?: number } {
        let calculatedIndice = 0 ;
        let delta = null;

        if (this.isMJP) {
            return { indice: indice };
        }

        switch(offsetCategory) {
            case OffsetCategory.pri:
                calculatedIndice = prevIndice / grandPrevIndice;
                break;
            case OffsetCategory.ipp	:
                delta = grandPrevIndice * (2 / 3) + prevIndice * (1 / 3);
                prevDelta = prevDelta != null ? prevDelta : delta;

                calculatedIndice = delta / prevDelta;
                prevDelta = delta;
                break;
            default:
                return { indice: indice };
        }

        return {
            indice: calculatedIndice - 1,
            delta: delta
        };
    }

    /*
     * Begin loop at edited part level
     * Calculate amounts of each year of editedPart
     * Go down to children and do it again
     */
    private processPartitionsAmounts(partitions: Partition[], editedPartCode: string,  parentCode?: string): void {
        // map trough partitions and calculate amounts based on cursors values
        partitions.map(p => {
            if((!parentCode && p.partitionDetails.code === editedPartCode)
                || (parentCode && p.parentCode === parentCode)) {
                this.processPartYears(p);
                this.processPartitionsAmounts(partitions, editedPartCode, p.partitionDetails.code);
            }
        });
    }

    /*
     * Calculate each estimated year of selected partition based on projectionRate and indice
     */
    private processPartYears(partition: Partition): void {
        // used to calculate special partitions where the original amount != 0% (like IPP or PRI)
        let prevDelta: number = null;
        const offset = partition.cursorParameters.offset;
        const offsetCategory = partition.partitionDetails.offsetCategory;
        const projectionRate = partition.projectionRate.rate;

        partition.simulationValues.years.map((year, i, years) => {
            if(!year.isEstimated) {
                year.indice = 0;
                return year;
            }

            const prevIndice: number = years[i - 1].indice + offset;
            const grandPrevIndice: number = years[i - 2].indice + offset;
            const calculatedIndice = this.calculateIndice(year.indice, offsetCategory, prevIndice, grandPrevIndice, prevDelta);
            const formattedIndice = calculatedIndice.indice;

            if (this.isMJP) {
                if (!year.isAmountEdited) {
                    const newAmount: number = this.calculateAmount(year.originalAmount, year.isRateNeutralized ? 0 : projectionRate, formattedIndice);
                    year.amount = this.formatSrv.precision(newAmount, 2);
                }
            } else {
                const newAmount: number = this.calculateAmount(years[i - 1].amount, projectionRate, formattedIndice);
                year.amount = this.formatSrv.precision(newAmount, 2);
            }

            prevDelta = calculatedIndice.delta;
        });
    }

    /**
     * Go deep to last parent level
     * Sum all children and go one step over each time
     * @param partitions
     * @param parentCode
     */
    private processAggregats(partitions: Partition[], parentCode?: string): void {
        partitions
        .map(partition => {
            const partCode = partition.partitionDetails.code;

            if((!parentCode && !partition.parentCode) || partition.parentCode === parentCode) {
                if(partitions.some(p => p.parentCode === partCode)) {
                    // Process children first
                    this.processAggregats(partitions, partCode);

                    // reduce all children years to sum all amounts in one years array
                    const totYears: SimulationYear[] = partitions
                        .filter(p =>
                            p.parentCode === partCode &&
                            p.isInTotal &&
                            (
                                p.simulationValues.flag !== SimulationFlagType.virtual ||
                                p.partitionDetails.partitionType === PartitionType.other ||
                                p.partitionDetails.partitionType === PartitionType.invest ||
                                p.partitionDetails.partitionType === PartitionType.funding ||
                                p.partitionDetails.partitionType === PartitionType.simulationFile)
                            )
                        .reduce((tots: SimulationYear[], part: Partition) => {

                            part.simulationValues.years
                            .filter(y => y.isEstimated)
                            .map(partYear => {
                                let yearFound = tots.find(y => y.year === partYear.year);
                                if(!yearFound) {
                                    tots.push({ ...partYear });
                                    yearFound = partYear;
                                } else {
                                    yearFound.amount += partYear.amount;
                                }

                                if (partYear.isRateNeutralized && !partYear.isAmountEdited) {
                                    yearFound.amount += partYear.additionalAmount;
                                }
                            });

                            return [...tots];
                        }, []);

                    // Apply sums to part amounts
                    partition.simulationValues.years.map(year => {
                        const totFound = totYears.find(tot => tot.year === year.year);
                        if(totFound && !year.isAmountEdited) {
                            year.amount = totFound.amount;
                        }
                    });
                }
            }
        });
    }

    private processKpis(simulation: Simulation): void {
        this.cumul = 0;

        simulation.kpisByYear.map(year => {
            if(year.isEstimated) {
                switch(simulation.providerCode) {
                    case 'bbc2020':
                        this.calcBBC2020kpis(year, simulation);
                        break;
                    default:
                        this.calcSICkpis(year, simulation.partitions);
                }
            } else {
                // get cumul from executed years
                const cumulKpi = year.kpis.find(k => k.code === 'cumul');
                this.cumul = (cumulKpi && cumulKpi.amount) ? cumulKpi.amount.value : 0;
            }
        });
    }

    private calcSICkpis(year: KpiByYear, partitions: Partition[]): void {
        const receipts: number = this.getPartitionYearAmount(partitions, 'recettes', year.year);
        const spending: number = this.getPartitionYearAmount(partitions, 'depenses', year.year);
        const prelevReceipt: number = this.getPartitionYearAmount(partitions, 'recettes.prelevements', year.year);
        const prelevSpending: number = this.getPartitionYearAmount(partitions, 'depenses.prelevements', year.year);
        const contributions: number = this.getPartitionYearAmount(partitions, 'cotisationsResponsabilisation', year.year);

        // prior: cumul N-1 - cotisationsResponsabilisation N
        const prior = this.cumul - contributions;

        // cumul: cumul année précédente + résultat année courante - cotisationsResponsabilisation
        // ratio: amount / recettes
        this.cumul += receipts - spending + (prelevReceipt - prelevSpending) - contributions;

        // balance: recettes - dépenses
        // ratio: amount / recettes
        const balanceAmount = receipts - spending;

        year.kpis.map(kpi => {
            switch(kpi.code) {
                case 'cumul':
                    kpi.amount.value = this.cumul;
                    kpi.ratio.value = kpi.amount.value / receipts;
                    break;
                case 'balance':
                    kpi.amount.value = balanceAmount;
                    kpi.ratio.value = kpi.amount.value / receipts;
                    break;
                case 'prior':
                    kpi.amount.value = prior;
                    break;
            }
        });
    }

    private calcBBC2020kpis(year: KpiByYear, simulation: Simulation): void {
        const partitions = simulation.partitions;

        const ontvangsten = this.getPartitionYearAmount(partitions, 'ontvangsten', year.year);
        const uitgaven = this.getPartitionYearAmount(partitions, 'uitgaven', year.year);

        const ontvangstenExploitatie = this.getPartitionYearAmount(partitions, 'ontvangsten.exploitatie', year.year);
        const uitgavenExploitatie = this.getPartitionYearAmount(partitions, 'uitgaven.exploitatie', year.year);

        const ontvangstenInvest = this.getPartitionYearAmount(partitions, 'ontvangsten.investering', year.year);
        const uitgavenInvest = this.getPartitionYearAmount(partitions, 'uitgaven.investering', year.year);

        const andereKostenSchulden = this.getPartitionYearAmount(partitions, 'uitgaven.exploitatie.fin.andereFinUitgaven.andereKostenSchulden', year.year);
        const rente = this.getPartitionYearAmount(partitions, 'uitgaven.exploitatie.fin.rente', year.year);
        const intrestSubsidies = this.getPartitionYearAmount(partitions, 'ontvangsten.exploitatie.fin.IntrestSubsidies', year.year);
        const terugVorderingen = this.getPartitionYearAmount(partitions, 'ontvangsten.exploitatie.fin.terugVorderingen', year.year);

        const uitPeriodieke = this.getPartitionYearAmount(partitions, 'uitgaven.financiering.vereffeningFinSchulden.periodieke', year.year);
        const ontPeriodieke = this.getPartitionYearAmount(partitions, 'ontvangsten.financiering.vereffening.terugvordering.periodieke', year.year);
        const federaleVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.federale.vorderingPref', year.year);
        const vlaamseVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.vlaamse.vorderingPref', year.year);
        const provincieVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.provincie.vorderingPref', year.year);
        const gemeenteVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.gemeente.vorderingPref', year.year);
        const ocmwVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.OCMW.vorderingPref', year.year);
        const andereVorderingPref = this.getPartitionYearAmount(partitions, 'ontvangsten.investering.subsidies.andere.vorderingPref', year.year);

        const exploitatieSaldo = ontvangstenExploitatie - uitgavenExploitatie;
        const investeringsSaldo = ontvangstenInvest - uitgavenInvest;
        const saldoExploitatieEnInvest = exploitatieSaldo + investeringsSaldo;
        const budgettairResultaat = ontvangsten - uitgaven;

        const delta = (andereKostenSchulden + rente - intrestSubsidies - terugVorderingen);
        const alpha = (uitPeriodieke - ontPeriodieke - federaleVorderingPref - vlaamseVorderingPref
                        - provincieVorderingPref - gemeenteVorderingPref - ocmwVorderingPref - andereVorderingPref);
        const explsaldoZonderInvest = exploitatieSaldo + delta;
        const afm = exploitatieSaldo - alpha;

        /* Schuldenlast =
            (uitgaven.financiering.vereffeningFinSchulden.periodieke
            - ontvangsten.financiering.vereffening.terugvordering.periodieke
            - ontvangsten.investering.subsidies.federale.vorderingPref
            - ontvangsten.investering.subsidies.vlaamse.vorderingPref
            - ontvangsten.investering.subsidies.provincie.vorderingPref
            - ontvangsten.investering.subsidies.gemeente.vorderingPref
            - ontvangsten.investering.subsidies.OCMW.vorderingPref
            - ontvangsten.investering.subsidies.andere.vorderingPref)
           +
           (uitgaven.exploitatie.fin.andereFinUitgaven.andereKostenSchulden
            + uitgaven.exploitatie.fin.rente
            - ontvangsten.exploitatie.fin.IntrestSubsidies
            - ontvangsten.exploitatie.fin.terugVorderingen)
        */
       const schuldenlast = alpha + delta;

        year.kpis.map(kpi => {
            switch(kpi.code) {
                case 'bbc2020.exploitatieSaldo.zonderInteresten':
                    kpi.amount.value = explsaldoZonderInvest;
                    break;
                case 'bbc2020.exploitatieSaldo':
                    kpi.amount.value = exploitatieSaldo;
                    break;
                case 'bbc2020.investeringsSaldo':
                    kpi.amount.value = investeringsSaldo;
                    break;
                case 'bbc2020.saldoExploitatieEnInvesteringen':
                    kpi.amount.value = saldoExploitatieEnInvest;
                    break;
                case 'bbc2020.budgettairResultaat':
                    kpi.amount.value = budgettairResultaat;
                    break;
                case 'bbc2020.afm':
                    kpi.amount.value = afm;
                    kpi.ratio.value = afm / explsaldoZonderInvest;
                    if (afm < 0 && kpi.ratio.value > 0) {
                        kpi.ratio.value = kpi.ratio.value * -1;
                    }
                    break;
                case 'bbc2020.schuldenlast':
                    kpi.amount.value = schuldenlast;
                    kpi.ratio.value = schuldenlast / explsaldoZonderInvest;
                    break;
                case 'bbc2020.gecumuleerdBudgettairResultaat':
                    if (year.isEstimated && kpi.ratio.value < 0.5) {
                        const previousKpis = simulation.kpisByYear.find(y => y.year === year.year - 1)?.kpis;
                        if (previousKpis) {
                            kpi.amount.value =
                                previousKpis.find(k => k.code === 'bbc2020.budgettairResultaat'           ).amount.value
                            +   previousKpis.find(k => k.code === 'bbc2020.gecumuleerdBudgettairResultaat').amount.value;
                        }
                    }
                    break;
            }
        });

        const obg   = year.kpis.find(k => k.code === 'bbc2020.onbeschikbareGelden'           );
        const gbr   = year.kpis.find(k => k.code === 'bbc2020.gecumuleerdBudgettairResultaat');
        const bbr   = year.kpis.find(k => k.code === 'bbc2020.beschikbaarBudgettairResultaat');
        const cumul = year.kpis.find(k => k.code === 'bbc2020.cumul'                         );

        cumul.amount.value = gbr.amount.value + budgettairResultaat;
        bbr  .amount.value = cumul.amount.value - (obg.amount?.value ?? 0);
    }

    private processM2(simulation: Simulation): void {
        simulation.schemaM2.years.map(year => {
            const kpiYear = simulation.kpisByYear.find(kpiY => kpiY.year === year.year);
            year.items.map(yearItem => {
                const kpiFound = kpiYear.kpis.find(kpi => kpi.code === yearItem.code);
                if(kpiFound) {
                    yearItem.value = kpiFound.amount.value;
                } else {
                    const partFound = simulation.partitions.find(p => p.partitionDetails.code === yearItem.code);
                    if(partFound) {
                        const yearFound = partFound.simulationValues.years.find(y => y.year === year.year);
                        if(yearFound) {
                            yearItem.value = yearFound.amount;
                        }
                    }
                }
            });
        });
    }

    private getPartitionYearAmount(partitions: Partition[], partitionCode: string, year: number): number {
        const partFound = partitions.find(p => p.partitionDetails.code === partitionCode);

        if (partFound) {
            return partFound.simulationValues.years.find(y => y.year === year).amount;
        }

        return 0;
    }
}
